/**
 * One-shot jump target set by search / traceability before opening a
 * workshop surface. Consumers (Decision Log, Captures, Asset Locker)
 * read + clear on open so the clicked hit scrolls into view (and, for
 * captures/uploads, opens its preview).
 */

import { create } from "zustand";
import type { SearchCategory } from "@/lib/search";

export type JumpTarget = {
  workshopId: string;
  category: SearchCategory;
  id: string;
};

type JumpTargetState = {
  target: JumpTarget | null;
  set: (target: JumpTarget) => void;
  /** Read-and-clear — returns the pending target if it matches `workshopId`. */
  take: (workshopId: string) => JumpTarget | null;
  clear: () => void;
};

export const useJumpTarget = create<JumpTargetState>((set, get) => ({
  target: null,
  set: (target) => set({ target }),
  take: (workshopId) => {
    const current = get().target;
    if (!current || current.workshopId !== workshopId) return null;
    set({ target: null });
    return current;
  },
  clear: () => set({ target: null }),
}));

/** Which Decision Log tab a category lives on. */
export function logTabForCategory(
  category: SearchCategory,
): "core" | "knowledge" | "notes" | null {
  if (
    category === "decision" ||
    category === "action" ||
    category === "assumption"
  ) {
    return "core";
  }
  if (
    category === "finding" ||
    category === "opportunity" ||
    category === "recommendation"
  ) {
    return "knowledge";
  }
  if (category === "note") return "notes";
  return null;
}
